import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialogContent } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatList, MatListItem } from '@angular/material/list';
import { Account } from '@api/core';
import { shouldBeNumberValidator } from '@shared/forms/forms.validators';
import { SharedStore } from '@shared/store/shared.signal-store';
import { isNotEmpty, trackById } from '@shared/utils/filters';
import { matchTextFilter } from '@shared/utils/strings';
import { asOptional, isDefined } from '@stillmandigital/common';
import { toViewReferralAgentRateValue } from 'app/settings/store/referral-agents/referral-agents.utils';
import { BehaviorSubject, combineLatest, map, startWith } from 'rxjs';

@Component({
  selector: 'app-link-account',
  templateUrl: './link-account.component.html',
  styleUrls: ['./link-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatLabel,
    MatFormField,
    ReactiveFormsModule,
    MatInput,
    MatList,
    MatListItem,
    AsyncPipe,
    MatButton,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatDialogContent,
    MatIcon,
    MatIconButton,
    MatSuffix,
  ],
})
export class LinkAccountComponent {
  private readonly _sharedStore = inject(SharedStore);
  private readonly _fb = inject(NonNullableFormBuilder);

  public readonly detailsForm = this._fb.group({
    name: this._fb.control<string | undefined>({
      value: undefined,
      disabled: true,
    }),
    commissionRate: this._fb.control<number | undefined>({
      value: undefined,
      disabled: true,
    }),
  });

  public readonly linkedAccounts$ = toObservable(this._sharedStore.linkedAccounts);

  public readonly unlinkedAccounts$ = new BehaviorSubject<Account[]>([]);

  public linkAccountForm = this._fb.group({
    account: [asOptional<number>(), [Validators.required, shouldBeNumberValidator()]],
  });

  public readonly hasLinkAccountValue$ = this.linkAccountForm.controls.account.valueChanges.pipe(
    startWith(undefined),
    map(isNotEmpty),
  );

  public readonly filteredUnlinkedWallets$ = combineLatest([
    this.unlinkedAccounts$,
    this.linkAccountForm.controls.account.valueChanges.pipe(
      startWith(undefined),
      map(code => matchTextFilter(code)),
    ),
  ]).pipe(map(([wallets, match]) => wallets.filter(wallet => match(wallet.name))));

  public displayAccount = (id: number) =>
    this.unlinkedAccounts$.value.find(account => account.id === id)?.name || id?.toString();

  public readonly trackById = trackById;

  private readonly _initFormValue = effect(() => {
    const referralAgent = this._sharedStore.selectedReferralAgent();

    const formValue = {
      name: referralAgent?.name,
      commissionRate: toViewReferralAgentRateValue(referralAgent?.commissionRate),
    };

    this.detailsForm.reset(formValue);
  });

  private readonly _initUnlinkedAccounts = effect(() =>
    this.unlinkedAccounts$.next(this._sharedStore.unlinkedAccounts()),
  );

  public addLink() {
    const { account: accountId } = this.linkAccountForm.value;
    const referralAgentId = this._sharedStore.selectedReferralAgent()?.id;

    if (isDefined(accountId) && isDefined(referralAgentId)) {
      this._sharedStore.updateAccount({ accountId, referralAgentId });
      this.linkAccountForm.reset();
    }
  }

  public removeLink(accountId: number | undefined) {
    if (isDefined(accountId)) {
      this._sharedStore.updateAccount({ accountId, referralAgentId: undefined });
    }
  }

  public resetLinkAccount() {
    this.linkAccountForm.reset();
  }
}
